import { type FormEvent, useState } from "react";
import { Link } from "react-router";

import { type QueueMetrics, type QueueRow, getMetrics, listQueue, message } from "./reviewClient";

function hours(seconds: number | null): string {
  return seconds === null ? "None" : `${(seconds / 3600).toFixed(1)} hours`;
}

/** Open cases for an engagement, with the signals that put each one where it is. */
export function ReviewQueuePage() {
  const [rows, setRows] = useState<QueueRow[] | null>(null);
  const [metrics, setMetrics] = useState<QueueMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  function onFind(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const engagement = String(new FormData(event.currentTarget).get("engagement") ?? "").trim();
    Promise.all([listQueue(engagement), getMetrics(engagement)])
      .then(([found, measured]) => {
        setRows(found);
        setMetrics(measured);
        setError(null);
      })
      .catch((caught: unknown) => setError(message(caught)));
  }

  return (
    <section aria-labelledby="queue-title">
      <h1 id="queue-title">Review queue</h1>
      <form onSubmit={onFind} aria-label="Find review cases">
        <label>
          Engagement id <input name="engagement" required />
        </label>
        <button type="submit">Show queue</button>
      </form>
      {error && <p role="alert">Could not load: {error}</p>}
      {metrics && (
        <p>
          {metrics.open_cases} open, oldest {hours(metrics.oldest_age_seconds)}, median{" "}
          {hours(metrics.median_age_seconds)}, {metrics.closed_last_day} closed in the last day.
        </p>
      )}
      {rows?.length === 0 && <p>No open cases for this engagement.</p>}
      {rows && rows.length > 0 && (
        <ol aria-label="Open cases">
          {rows.map((row) => (
            <li key={row.case_id}>
              <Link to={`/review/${row.case_id}`}>Case for {row.entry_reason}</Link> (score{" "}
              {row.score})
              <ul aria-label="Why it is here">
                {row.signals.map((signal) => (
                  <li key={signal.name}>
                    {signal.name} (+{signal.weight}): {signal.detail}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
